import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { ICategory } from '../../types/damTypes';
import BarcodePreview from './BarcodePreview';
import BarcodeSettings from './BarcodeSettings';
import { batchGenerateBarcodes, getPreviewSvg } from './barcodeApi';
import { SavedBarcodePreset, loadBarcodePresets } from './presetStorage';
import {
  BatchBarcodeResultItem,
  BatchGenerateResponse,
  BatchBarcodeStatus,
  BarcodeOptions,
  DEFAULT_OPTIONS,
} from './types';

const CATEGORY_SETTINGS_KEY = 'code-generator-category-settings';

interface BulkBarcodeDialogProps {
  open: boolean;
  selectedAssetIds: string[];
  categories: ICategory[];
  onClose: () => void;
  onComplete?: () => void;
}

function loadSavedEanCategorySettings(): { mainCategoryId: string; subCategoryId: string } {
  try {
    const parsed = JSON.parse(localStorage.getItem(CATEGORY_SETTINGS_KEY) || '{}');
    return {
      mainCategoryId: parsed?.ean?.mainCategoryId || '',
      subCategoryId: parsed?.ean?.subCategoryId || '',
    };
  } catch {
    return {
      mainCategoryId: '',
      subCategoryId: '',
    };
  }
}

function getSubcategories(categories: ICategory[], parentId: string): ICategory[] {
  return categories.filter((category) => {
    if (!category.parentCategory) return false;
    const resolvedParentId = typeof category.parentCategory === 'string'
      ? category.parentCategory
      : category.parentCategory._id;
    return resolvedParentId === parentId;
  });
}

function statusChipColor(status: BatchBarcodeStatus): 'success' | 'warning' | 'error' | 'default' {
  if (status === 'ready' || status === 'generated') return 'success';
  if (status === 'no_ean') return 'warning';
  if (status === 'invalid' || status === 'error') return 'error';
  return 'default';
}

function statusLabel(status: BatchBarcodeStatus): string {
  switch (status) {
    case 'ready':
      return 'Redo';
    case 'generated':
      return 'Genererad';
    case 'no_ean':
      return 'Saknar EAN';
    case 'invalid':
      return 'Ogiltig';
    case 'error':
      return 'Fel';
    default:
      return status;
  }
}

function pickPreviewEan(result: BatchGenerateResponse | null): string | null {
  if (!result) return null;
  const item = result.results.find((row) => row.status === 'generated')
    || result.results.find((row) => row.status === 'ready');
  return item?.resolvedEan || null;
}

function renderSourceSummary(row: BatchBarcodeResultItem): string {
  if (row.sourceField && row.rawValue) {
    return `${row.sourceField}: ${row.rawValue}`;
  }
  if (row.sourceField) {
    return row.sourceField;
  }
  return 'Ingen källa';
}

function areBarcodeOptionsEqual(a: BarcodeOptions, b: BarcodeOptions): boolean {
  return (
    a.magnification === b.magnification &&
    a.barHeight === b.barHeight &&
    a.showText === b.showText &&
    a.showGuardExtensions === b.showGuardExtensions &&
    a.showQuietZones === b.showQuietZones &&
    a.dpi === b.dpi &&
    a.lightMarginIndicator === b.lightMarginIndicator &&
    a.cropLeft === b.cropLeft
  );
}

const BulkBarcodeDialog: React.FC<BulkBarcodeDialogProps> = ({
  open,
  selectedAssetIds,
  categories,
  onClose,
  onComplete,
}) => {
  const [options, setOptions] = React.useState<BarcodeOptions>(DEFAULT_OPTIONS);
  const [mainCategoryId, setMainCategoryId] = React.useState('');
  const [subCategoryId, setSubCategoryId] = React.useState('');
  const [presets, setPresets] = React.useState<SavedBarcodePreset[]>([]);
  const [previewResult, setPreviewResult] = React.useState<BatchGenerateResponse | null>(null);
  const [executionResult, setExecutionResult] = React.useState<BatchGenerateResponse | null>(null);
  const [loadingPreview, setLoadingPreview] = React.useState(false);
  const [executing, setExecuting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [previewSvg, setPreviewSvg] = React.useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const requestSequence = React.useRef(0);

  React.useEffect(() => {
    if (!open) return;

    const saved = loadSavedEanCategorySettings();
    setPresets(loadBarcodePresets());
    setOptions(DEFAULT_OPTIONS);
    setMainCategoryId(saved.mainCategoryId);
    setSubCategoryId(saved.subCategoryId);
    setPreviewResult(null);
    setExecutionResult(null);
    setError(null);
    setPreviewSvg(null);
    setPreviewError(null);
  }, [open]);

  const categoryIds = React.useMemo(() => {
    const ids = [];
    if (mainCategoryId) ids.push(mainCategoryId);
    if (subCategoryId) ids.push(subCategoryId);
    return ids;
  }, [mainCategoryId, subCategoryId]);

  const categorySignature = React.useMemo(() => categoryIds.join(','), [categoryIds]);
  const activeResult = executionResult || previewResult;
  const previewEan = React.useMemo(() => pickPreviewEan(activeResult), [activeResult]);
  const readyCount = previewResult?.summary.ready || 0;
  const selectedPresetValue = React.useMemo(() => {
    if (areBarcodeOptionsEqual(options, DEFAULT_OPTIONS)) {
      return 'standard';
    }

    const matchedPreset = presets.find((preset) => areBarcodeOptionsEqual(options, { ...DEFAULT_OPTIONS, ...preset.options }));
    return matchedPreset?.id || '__custom__';
  }, [options, presets]);

  React.useEffect(() => {
    if (!open) return;
    setExecutionResult(null);
  }, [open, options, categorySignature, selectedAssetIds]);

  React.useEffect(() => {
    if (!open || selectedAssetIds.length === 0 || executionResult) {
      return;
    }

    const currentRequest = ++requestSequence.current;
    const timeoutId = window.setTimeout(async () => {
      setLoadingPreview(true);
      setError(null);

      try {
        const result = await batchGenerateBarcodes({
          assetIds: selectedAssetIds,
          options,
          categoryIds: categoryIds.length > 0 ? categoryIds : undefined,
          dryRun: true,
        });

        if (requestSequence.current !== currentRequest) return;
        setPreviewResult(result);
      } catch (err: any) {
        if (requestSequence.current !== currentRequest) return;
        setPreviewResult(null);
        setError(err?.response?.data?.error || err?.message || 'Kunde inte läsa batchförhandsgranskningen.');
      } finally {
        if (requestSequence.current === currentRequest) {
          setLoadingPreview(false);
        }
      }
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [open, selectedAssetIds, options, categoryIds, executionResult]);

  React.useEffect(() => {
    if (!open || !previewEan) {
      setPreviewSvg(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);

    getPreviewSvg(previewEan, options)
      .then((svg) => {
        if (cancelled) return;
        setPreviewSvg(svg);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setPreviewSvg(null);
        setPreviewError(err?.response?.data?.error || err?.message || 'Kunde inte ladda förhandsgranskning.');
      })
      .finally(() => {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, previewEan, options]);

  const handleGenerate = async () => {
    setExecuting(true);
    setError(null);

    try {
      const result = await batchGenerateBarcodes({
        assetIds: selectedAssetIds,
        options,
        categoryIds: categoryIds.length > 0 ? categoryIds : undefined,
        dryRun: false,
        enqueueAi: false,
      });

      setExecutionResult(result);
      if (result.summary.generated > 0) {
        onComplete && onComplete();
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Kunde inte batchgenerera EAN-koder.');
    } finally {
      setExecuting(false);
    }
  };

  const currentCategoryName = categories.find(
    (category) => category._id === (subCategoryId || mainCategoryId),
  )?.name;

  const resultRows = activeResult?.results || [];
  const mainCategories = React.useMemo(
    () => categories.filter((category) => !category.parentCategory),
    [categories],
  );
  const subCategories = React.useMemo(
    () => getSubcategories(categories, mainCategoryId),
    [categories, mainCategoryId],
  );

  const handlePresetChange = (presetId: string) => {
    if (presetId === 'standard') {
      setOptions(DEFAULT_OPTIONS);
      return;
    }

    const preset = presets.find((item) => item.id === presetId);
    if (!preset) return;
    setOptions({ ...DEFAULT_OPTIONS, ...preset.options });
  };

  return (
    <Dialog open={open} onClose={executing ? undefined : onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Batchgenerera EAN-koder</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {(loadingPreview || executing) && <LinearProgress />}

        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {selectedAssetIds.length} valda produkter analyseras i backend. Produkten måste ha ett giltigt GTIN/EAN i metadata för att bli körbar.
          </Typography>
        </Box>

        {error && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 2, flexDirection: { xs: 'column', lg: 'row' } }}>
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Förhandsgranskning
              </Typography>
              <BarcodePreview svg={previewSvg} loading={previewLoading} error={previewError} />
              {previewEan && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Exempel-EAN: <Box component="span" sx={{ fontFamily: 'monospace' }}>{previewEan}</Box>
                </Typography>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2">
                  {executionResult ? 'Körresultat' : 'Dry-run resultat'}
                </Typography>
                {activeResult?.summary && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip size="small" color="success" variant="outlined" label={`Redo ${activeResult.summary.ready}`} />
                    <Chip size="small" color="success" variant="outlined" label={`Genererade ${activeResult.summary.generated}`} />
                    <Chip size="small" color="warning" variant="outlined" label={`Saknar EAN ${activeResult.summary.noEan}`} />
                    <Chip size="small" color="error" variant="outlined" label={`Ogiltiga ${activeResult.summary.invalid}`} />
                    <Chip size="small" color="default" variant="outlined" label={`Ersatta ${activeResult.summary.replaced}`} />
                    <Chip size="small" color="error" variant="outlined" label={`Fel ${activeResult.summary.failed}`} />
                  </Box>
                )}
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              {loadingPreview && !activeResult ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, justifyContent: 'center' }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Läser metadata och validerar EAN-koder...
                  </Typography>
                </Box>
              ) : resultRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Inga resultat ännu.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 460, overflow: 'auto' }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1.7fr 1.2fr 0.9fr 1.7fr', md: '2fr 1.2fr 0.9fr 2fr' },
                      gap: 1,
                      pb: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Produkt</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>EAN</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Status</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Källa / meddelande</Typography>
                  </Box>

                  {resultRows.map((row) => (
                    <Box
                      key={row.assetId}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1.7fr 1.2fr 0.9fr 1.7fr', md: '2fr 1.2fr 0.9fr 2fr' },
                        gap: 1,
                        py: 1.25,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        alignItems: 'start',
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {row.assetId}
                        </Typography>
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {row.resolvedEan || '–'}
                        </Typography>
                        {row.replaced && (
                          <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                            Befintlig asset ersattes
                          </Typography>
                        )}
                      </Box>

                      <Box>
                        <Chip size="small" color={statusChipColor(row.status)} label={statusLabel(row.status)} />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {renderSourceSummary(row)}
                        </Typography>
                        {row.message && (
                          <Typography variant="body2" sx={{ mt: 0.25 }}>
                            {row.message}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Box>

          <Box sx={{ flex: '0 0 360px', minWidth: { xs: '100%', lg: 360 } }}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                EAN-mall
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel>Mall</InputLabel>
                <Select
                  value={selectedPresetValue}
                  label="Mall"
                  onChange={(event) => handlePresetChange(event.target.value)}
                >
                  <MenuItem value="standard">Grundinställning</MenuItem>
                  {presets.map((preset) => (
                    <MenuItem key={preset.id} value={preset.id}>
                      {preset.name}{preset.locked ? ' (låst)' : ''}
                    </MenuItem>
                  ))}
                  {selectedPresetValue === '__custom__' && (
                    <MenuItem value="__custom__" disabled>
                      Anpassade inställningar
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {presets.length > 0
                  ? 'Batchläget använder samma sparade EAN-mallar som den vanliga EAN-generatorn.'
                  : 'Inga sparade EAN-mallar hittades ännu. Skapa dem i den vanliga EAN-generatorn så blir de tillgängliga här.'}
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Kategori för genererade EAN-koder
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>Huvudkategori</InputLabel>
                <Select
                  value={mainCategoryId}
                  label="Huvudkategori"
                  onChange={(event) => {
                    setMainCategoryId(event.target.value);
                    setSubCategoryId('');
                  }}
                >
                  <MenuItem value="">Standard (EAN-Koder)</MenuItem>
                  {mainCategories.map((category) => (
                    <MenuItem key={category._id} value={category._id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {mainCategoryId && subCategories.length > 0 && (
                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                  <InputLabel>Underkategori</InputLabel>
                  <Select
                    value={subCategoryId}
                    label="Underkategori"
                    onChange={(event) => setSubCategoryId(event.target.value)}
                  >
                    <MenuItem value="">Ingen underkategori</MenuItem>
                    {subCategories.map((category) => (
                      <MenuItem key={category._id} value={category._id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {currentCategoryName
                  ? `Genererade assets sparas i: ${currentCategoryName}`
                  : 'Om ingen kategori väljs används standardkategorin EAN-Koder.'}
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <BarcodeSettings options={options} onChange={setOptions} />
            </Paper>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={executing}>
          Stäng
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={executing || loadingPreview || readyCount === 0}
          startIcon={executing ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {executing ? 'Genererar...' : `Generera ${readyCount} EAN-koder`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkBarcodeDialog;
